import { lazy } from "react";

import { RoutePathBuilder } from "@/common/builders/routePath";
import AuthorizedRoute from "@/common/components/Auth/AuthorizedRoute";
import { Loadable } from "@/common/components/Loadable";
import { ROUTE_PATH } from "@/common/constants/routing";
import { AppPermission } from "@/core/api/generated/v1";

const CompanyProfileEditPage = Loadable(
  lazy(() => import("../../MainAppView/Management/CompanyProfile/CompanyProfileEditPage")),
);
const CompanyProfileViewPage = Loadable(
  lazy(() => import("../../MainAppView/Management/CompanyProfile/CompanyProfileViewPage")),
);
const CreateUpdateImportPage = Loadable(
  lazy(() => import("../../MainAppView/Management/DataImport/CreateUpdateImportPage")),
);
const ImportViewPage = Loadable(
  lazy(() => import("../../MainAppView/Management/DataImport/ImportViewPage")),
);
const ImportsPage = Loadable(
  lazy(() => import("../../MainAppView/Management/DataImport/ImportsPage")),
);

const IntegrationApiClientCreateUpdatePage = Loadable(
  lazy(
    () =>
      import("../../MainAppView/Management/Integration/IntegrationApiClients/IntegrationApiClientCreateUpdatePage"),
  ),
);
const IntegrationApiClientViewPage = Loadable(
  lazy(
    () =>
      import("../../MainAppView/Management/Integration/IntegrationApiClients/IntegrationApiClientViewPage"),
  ),
);
const IntegrationApiClientsPage = Loadable(
  lazy(
    () =>
      import("../../MainAppView/Management/Integration/IntegrationApiClients/IntegrationApiClientsPage"),
  ),
);
const IntegrationPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Integration/IntegrationPage")),
);
const TeslaConnectionViewPage = Loadable(
  lazy(
    () =>
      import("../../MainAppView/Management/Integration/TeslaConnections/TeslaConnectionViewPage"),
  ),
);
const TeslaConnectionsPage = Loadable(
  lazy(
    () => import("../../MainAppView/Management/Integration/TeslaConnections/TeslaConnectionsPage"),
  ),
);
const InviteCreatePersonalPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Invites/InviteCreatePersonalPage")),
);
const InviteCreateNewAnonymousPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Invites/InviteCreateNewAnonymousPage")),
);
const InviteViewPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Invites/InviteViewPage")),
);
const InvitesPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Invites/InvitesPage")),
);
const InvoiceViewPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Invoices/InvoiceViewPage")),
);
const InvoicesPage = Loadable(lazy(() => import("../../MainAppView/Management/Invoices/Invoices")));
const PaymentProfileEdit = Loadable(
  lazy(() => import("../../MainAppView/Management/PaymentProfile/Edit/Edit")),
);
const PaymentProfile = Loadable(
  lazy(() => import("../../MainAppView/Management/PaymentProfile/PaymentProfile")),
);
const RoleCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Management/Roles/RoleCreateUpdatePage")),
);
const RoleViewPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Roles/RoleViewPage")),
);
const RolesPage = Loadable(lazy(() => import("../../MainAppView/Management/Roles/RolesPage")));
const SubscriptionPlans = Loadable(
  lazy(() => import("../../MainAppView/Management/SubscriptionPlans/SubscriptionPlans")),
);
const Subscriptions = Loadable(
  lazy(() => import("../../MainAppView/Management/Subscriptions/Subscriptions")),
);
const EditTenantSettingsPage = Loadable(
  lazy(() => import("../../MainAppView/Management/TenantSettings/Edit/TenantSettingsUpdatePage")),
);
const UpdateUserRolesPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Users/UpdateUserRolesPage")),
);
const WebhookCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Management/Webhooks/WebhookCreateUpdatePage")),
);
const WebhookViewPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Webhooks/WebhookViewPage")),
);
const WebhooksPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Webhooks/WebhooksPage")),
);
const UserEdit = Loadable(lazy(() => import("../../MainAppView/Management/Users/UserEditPage")));
const UserViewPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Users/UserViewPage")),
);
const Users = Loadable(lazy(() => import("../../MainAppView/Management/Users/UsersPage")));

const LocationCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Locations/LocationCreateUpdatePage")),
);
const LocationsPage = Loadable(lazy(() => import("../../MainAppView/Locations/LocationsPage")));
const DepartmentCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Management/Departments/DepartmentCreateUpdatePage")),
);

const DepartmentsPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Departments/DepartmentsPage")),
);

const SpotCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Spots/SpotCreateUpdatePage")),
);
const SpotsPage = Loadable(lazy(() => import("../../MainAppView/Spots/SpotsPage")));
const LocationViewPage = Loadable(
  lazy(() => import("../../MainAppView/Locations/LocationViewPage")),
);
const DepartmentViewPage = Loadable(
  lazy(() => import("../../MainAppView/Management/Departments/DepartmentViewPage")),
);
const SpotViewPage = Loadable(lazy(() => import("../../MainAppView/Spots/SpotViewPage")));

export function ManagementRoutes() {
  return (
    <>
      {/* Subscriptions */}
      <AuthorizedRoute
        permissions={[AppPermission.TenantSubscriptionRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_SUBSCRIPTIONS)}
        render={() => <Subscriptions />}
      />

      {/* Subscription plans */}
      <AuthorizedRoute
        permissions={[AppPermission.TenantSubscriptionRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_SUBSCRIPTION_PLANS)}
        render={() => <SubscriptionPlans />}
      />

      {/* Tenant profile */}
      <AuthorizedRoute
        permissions={[AppPermission.TenantManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_TENANT_PROFILE)}
        render={() => <CompanyProfileViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TenantManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_TENANT_PROFILE_EDIT)}
        render={() => <CompanyProfileEditPage />}
      />

      {/* Payment profile */}
      <AuthorizedRoute
        permissions={[AppPermission.TenantSubscriptionRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_PAYMENT_PROFILE)}
        render={() => <PaymentProfile />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TenantSubscriptionManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_PAYMENT_PROFILE_EDIT)}
        render={() => <PaymentProfileEdit />}
      />

      {/* Settings */}
      <AuthorizedRoute
        permissions={[AppPermission.TenantManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_SETTINGS_EDIT)}
        render={() => <EditTenantSettingsPage />}
      />

      {/* Departments */}
      <AuthorizedRoute
        permissions={[AppPermission.DepartmentRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DEPARTMENTS)}
        render={() => <DepartmentsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.DepartmentRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DEPARTMENT_VIEW())}
        render={() => <DepartmentViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.DepartmentManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DEPARTMENT_CREATE)}
        render={() => <DepartmentCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.DepartmentManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.DEPARTMENT_EDIT())}
        render={() => <DepartmentCreateUpdatePage />}
      />

      {/* Locations */}
      <AuthorizedRoute
        permissions={[AppPermission.LocationRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.LOCATIONS)}
        render={() => <LocationsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.LocationRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.LOCATION_VIEW())}
        render={() => <LocationViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.LocationManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.LOCATION_CREATE)}
        render={() => <LocationCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.LocationManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.LOCATION_EDIT())}
        render={() => <LocationCreateUpdatePage />}
      />

      {/* Spots */}
      <AuthorizedRoute
        permissions={[AppPermission.SpotRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.SPOTS)}
        render={() => <SpotsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.SpotRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.SPOT_VIEW())}
        render={() => <SpotViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.SpotManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.SPOT_CREATE())}
        render={() => <SpotCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.SpotManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.SPOT_EDIT())}
        render={() => <SpotCreateUpdatePage />}
      />

      {/* Imports */}
      <AuthorizedRoute
        permissions={[
          AppPermission.ImportManage,
          AppPermission.ImportRead,
          AppPermission.FleetAppAccess,
        ]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_IMPORTS_CREATE())}
        render={() => <CreateUpdateImportPage />}
      />

      <AuthorizedRoute
        permissions={[
          AppPermission.ImportManage,
          AppPermission.ImportRead,
          AppPermission.FleetAppAccess,
        ]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_IMPORTS_EDIT())}
        render={() => <CreateUpdateImportPage />}
      />

      <AuthorizedRoute
        permissions={[AppPermission.ImportRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_IMPORTS)}
        render={() => <ImportsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.ImportRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_IMPORTS_VIEW())}
        render={({
          match: {
            params: { importId },
          },
        }) => {
          return <ImportViewPage importId={importId} />;
        }}
      />

      {/* Users */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.TenantUserRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_USERS)}
        render={() => {
          return <Users />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.TenantUserManage]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_USER_EDIT())}
        render={() => {
          return <UserEdit />;
        }}
      />
      <AuthorizedRoute
        permissions={[
          AppPermission.FleetAppAccess,
          AppPermission.RoleAssignmentManage,
          AppPermission.TenantUserRead,
        ]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_USER_ROLES_EDIT())}
        render={() => {
          return <UpdateUserRolesPage />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess, AppPermission.TenantUserRead]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_USER_VIEW())}
        render={() => <UserViewPage />}
      />

      {/* Invites */}
      <AuthorizedRoute
        permissions={[AppPermission.TenantInviteRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INVITES)}
        render={() => {
          return <InvitesPage />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TenantInviteRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INVITE_VIEW())}
        render={() => {
          return <InviteViewPage />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TenantInviteRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INVITES_CREATE_PERSONAL())}
        render={() => {
          return <InviteCreatePersonalPage />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TenantInviteRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INVITES_CREATE_ANONYMOUS())}
        render={() => {
          return <InviteCreateNewAnonymousPage />;
        }}
      />

      {/* Roles */}
      <AuthorizedRoute
        permissions={[AppPermission.TenantRoleRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_ROLES)}
        render={() => {
          return <RolesPage />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TenantRoleManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_ROLE_CREATE())}
        render={() => {
          return <RoleCreateUpdatePage />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TenantRoleManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_ROLE_EDIT())}
        render={() => {
          return <RoleCreateUpdatePage />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TenantRoleRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_ROLE_VIEW())}
        render={() => {
          return <RoleViewPage />;
        }}
      />

      {/* Invoices */}
      <AuthorizedRoute
        permissions={[AppPermission.InvoiceRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.INVOICES)}
        render={() => {
          return <InvoicesPage />;
        }}
      />
      <AuthorizedRoute
        permissions={[AppPermission.InvoiceRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.INVOICE_VIEW())}
        render={() => {
          return <InvoiceViewPage />;
        }}
      />

      {/* Integration */}
      <AuthorizedRoute
        permissions={[AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION)}
        render={() => <IntegrationPage />}
      />

      {/* IntegrationApiClient */}
      <AuthorizedRoute
        permissions={[AppPermission.IntegrationApiClientRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_API_CLIENTS)}
        render={() => <IntegrationApiClientsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.IntegrationApiClientRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_API_CLIENT_VIEW())}
        render={() => <IntegrationApiClientViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.IntegrationApiClientManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_API_CLIENT_CREATE)}
        render={() => <IntegrationApiClientCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.IntegrationApiClientManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_API_CLIENT_EDIT())}
        render={() => <IntegrationApiClientCreateUpdatePage />}
      />

      {/* Webhook */}
      <AuthorizedRoute
        permissions={[AppPermission.WebhookRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_WEBHOOKS)}
        render={() => <WebhooksPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.WebhookRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_WEBHOOK_VIEW())}
        render={() => <WebhookViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.WebhookManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_WEBHOOK_EDIT())}
        render={() => <WebhookCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.WebhookManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_WEBHOOK_CREATE)}
        render={() => <WebhookCreateUpdatePage />}
      />

      {/* TeslaConnection */}
      <AuthorizedRoute
        permissions={[AppPermission.TeslaConnectionRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT_INTEGRATION_TESLA_CONNECTIONS)}
        render={() => <TeslaConnectionsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.TeslaConnectionRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(
          ROUTE_PATH.MANAGEMENT_INTEGRATION_TESLA_CONNECTION_VIEW(),
        )}
        render={() => <TeslaConnectionViewPage />}
      />
    </>
  );
}
